import React, { useEffect, useState } from 'react';
import Parse from '../../parseConfig';
import useUIPresentationMode from '../helpers/useUIPresentationMode';
import '../styles/SideBarLeft.css';
import '../styles/SideBarToggle.css';
import '../styles/phcolours.css';

import PlanHubLogo from '../../images/PlanHubLogoLighter.png';
import IconLayers from '../../images/IconLayers@3x.png';
import IconLayersActive from '../../images/IconLayersActive@3x.png';
import CollapseIconHover from '../../images/IconLeftArrowBlue@3x.png';
import CollapseIcon from '../../images/IconLeftArrowGrey@3x.png';


export default function UsageSideBarLeft({
  isVisible = true,
  onRequestClose = () => { },
  filterValue = 'all',
  setFilterValue = () => { },
  isLoading = false,
  loadError = null,
  totalCount = 0,
  filteredCount = 0,
  onLogout = () => { },
  availableStyles = [],
  selectedStyles = new Set(),
  setSelectedStyles = () => { },
  circleAnalysis = { hasCircle: false, topPlans: [] },
  onClearCircle = () => { },
  layers = { primary: [], secondary: [] },
  selectedBaseMap = 'None',
  setSelectedBaseMap = () => { },
  isParcelBoundariesVisible = false,
  toggleParcelBoundaries = () => { },
}) {

  const [isSideBarCollapseButtonHovering, setIsSideBarCollapseButtonHovering] = useState(false);
  const [userName, setUserName] = useState('');
  const [layersExpanded, setLayersExpanded] = useState(false);

  useEffect(() => {
    const currentUser = Parse.User.current();
    if (currentUser) {
      const fullName = `${currentUser.get('firstName')} ${currentUser.get('lastName')}`;
      setUserName(fullName.trim());
    }
  }, []);

  const { presentationModeSize } = useUIPresentationMode();
  const hideLogo = presentationModeSize === 5;

  const handleBaseMapChange = (event) => {
    const newBaseMap = event.target.value;
    setSelectedBaseMap(newBaseMap);
  };

  const timeframeOptions = [
    { label: 'Latest', value: 'latest' },
    { label: '30 days', value: '30' },
    { label: '60 days', value: '60' },
    { label: '6 months', value: '180' },
    { label: '1 year  (slow)', value: '365' },
    { label: 'All  (slowest!)', value: 'all' },
  ];

  return (
    <div
      className="sidebar"
      style={{
        width: '100%',
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRight: isVisible ? '0.75px solid #596A99' : 'none',
        transition: 'flex-basis 0.3s ease, opacity 0.3s ease',
        opacity: isVisible ? 1 : 0,
      }}
    >

      <button
        className="sidebar-toggle-button sidebar-left-toggle-button-expanded"
        onClick={onRequestClose}
        onMouseEnter={() => setIsSideBarCollapseButtonHovering(true)}
        onMouseLeave={() => setIsSideBarCollapseButtonHovering(false)}
      >
        <img
          src={isSideBarCollapseButtonHovering ? CollapseIconHover : CollapseIcon}
          alt="Hide Sidebar"
        />
      </button>

      <div className="sidebar-inner">

        {!hideLogo && (
          <div className="sidebar-logo-container">
            <img
              src={PlanHubLogo}
              alt="PlanHub Logo"
              className="sidebar-logo"
            />
          </div>
        )}

        <div
          className="sidebar-heading-row"
          onClick={() => setLayersExpanded(prev => !prev)}
          title="Toggle Layers Section"
          style={{ cursor: 'pointer' }}
        >
          <img
            src={layersExpanded ? IconLayersActive : IconLayers}
            alt="Layers Icon"
            className="sidebar-icon-left"
          />
          <span
            className={`sidebar-heading-label ${layersExpanded ? 'active' : 'inactive'}`}
          >
            Layers
          </span>
        </div>

        <div className={`collapsible-section ${layersExpanded ? 'expanded' : ''}`}>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={isParcelBoundariesVisible}
              onChange={(e) => toggleParcelBoundaries(e.target.checked)}
            />
            <span>Parcel Boundaries</span>
          </label>

          {Array.isArray(layers.secondary) && layers.secondary.map((layer) => (
            <label className="radio" key={layer.id}>
              <input
                type="radio"
                name="basemap"
                value={layer.get('name')}
                checked={selectedBaseMap === layer.get('name')}
                onChange={handleBaseMapChange}
              />
              <span>{layer.get('name')}</span>
            </label>
          ))}

          <label className="radio">
            <input
              type="radio"
              name="basemap"
              value="None"
              checked={selectedBaseMap === 'None'}
              onChange={handleBaseMapChange}
            />
            <span>Road Map</span>
          </label>
        </div>

        <div className="sidebar-heading-row" style={{ marginTop: 12 }}>
          <span className="sidebar-heading-label active">Usage</span>
        </div>

        <div style={{ marginTop: '12px' }}>

          <div style={{ fontWeight: 600, fontSize: 13, marginTop: '12px', marginBottom: '6px' }}>
            Style
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {availableStyles.map((style) => (
              <label key={style} className="checkbox" style={{ padding: 0, margin: '6px 0 3px 10px' }}>
                <input
                  type="checkbox"
                  checked={selectedStyles.has(style)}
                  onChange={() => {
                    const next = new Set(selectedStyles);
                    if (next.has(style)) {
                      next.delete(style);
                    } else {
                      next.add(style);
                    }
                    setSelectedStyles(next);
                  }}
                />
                <span>{style}</span>
              </label>
            ))}
          </div>

          <div style={{ fontWeight: 600, fontSize: 13, marginTop: '18px', marginBottom: '6px' }}>
            Timeframe
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {timeframeOptions.map(({ label, value }) => (
              <label key={value} className="radio" style={{ padding: 0, margin: '6px 0 3px 10px' }}>
                <input
                  type="radio"
                  name="createdAtFilter"
                  checked={filterValue === value}
                  onChange={() => setFilterValue(value)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>

        {isLoading && (
          <div style={{ marginTop: 20, fontSize: 13, color: 'var(--phgrey)' }}>
            Loading...
          </div>
        )}

        {loadError && (
          <div style={{ marginTop: 20, fontSize: 13, color: 'var(--phorangefont)' }}>
            Failed to load usage data.
          </div>
        )}

        {!isLoading && !loadError && (
          <div>
            <div style={{ marginTop: 20, fontSize: 13, color: 'var(--phblueaccent)' }}>
              Showing {filteredCount} of {totalCount}
            </div>

            {circleAnalysis.hasCircle && (
              <div style={{ marginTop: 50 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                  Popular Plans (50km)
                </div>
                <div style={{ fontSize: 13, color: 'var(--phblue)', lineHeight: 1.6, marginLeft: 10 }}>
                  <div>1 - {circleAnalysis.topPlans[0]?.name || 'N/A'}</div>
                  <div>2 - {circleAnalysis.topPlans[1]?.name || 'N/A'}</div>
                  <div>3 - {circleAnalysis.topPlans[2]?.name || 'N/A'}</div>
                </div>

                <div
                  className="sidebarRight-landinfo-row"
                  onClick={onClearCircle}
                  style={{
                    marginTop: 14,
                    justifyContent: 'center',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: 'var(--phwashedorangebackground)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--phorangefont)';
                    e.currentTarget.querySelector('span').style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--phwashedorangebackground)';
                    e.currentTarget.querySelector('span').style.color = 'var(--phorangefont)';
                  }}
                >
                  <span style={{ color: 'var(--phorangefont)', fontWeight: 500 }}>
                    Clear circle
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginTop: 50 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                Legend
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: '#00ff00', fontSize: 15, lineHeight: 1 }}>◉</span>
                  <span style={{ fontSize: 13, color: 'var(--phblue)' }}>Solid Timber</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: '#009cff', fontSize: 15, lineHeight: 1 }}>◉</span>
                  <span style={{ fontSize: 13, color: 'var(--phblue)' }}>Timber Framed</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: '#ff007f', fontSize: 15, lineHeight: 1 }}>◉</span>
                  <span style={{ fontSize: 13, color: 'var(--phblue)' }}>Accessory</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: '#ff8c00', fontSize: 15, lineHeight: 1 }}>◉</span>
                  <span style={{ fontSize: 13, color: 'var(--phblue)' }}>Transportables</span>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      <div className="sidebar-footer">
        {userName && (
          <div className="logout-button" style={{ cursor: 'default', pointerEvents: 'none' }}>
            {userName}
          </div>
        )}

        <button className="logout-button" onClick={onLogout}>
          Log Out
        </button>

        <div className="copyright">© PlanHub 2026</div>
      </div>
    </div>
  );
}
